import { Stack, Typography } from '@mui/material';
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { ButtonGroup } from '@/components/form/baseInputs/ButtonGroup';
import { ButtonGroupInputOption } from '@/components/form/baseInputs/inputTypes';
import { Card } from '@/components/layout/Card/Card';
import { TabsProvider } from '@/components/navigation/NavigationTabs/Tabs.provider';
import { useActiveTab } from '@/components/navigation/NavigationTabs/useActiveTab';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useModalState } from '@/hooks/useModalState';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { isBeneficiaryDesignationEntity } from '@/modules/dispositiveProvisions/dispositiveProvisions.label';
import { AddNotesToPresentationModal } from '@/modules/entities/components/EntityNotesPerDeath/AddNotesToPresentationModal';
import {
  EntityNotesPerDeathEditor,
  EntityNotesPerDeathEditorFooter,
  EntityNotesPerDeathEditorV2,
} from '@/modules/entities/components/EntityNotesPerDeath/EntityNotesPerDeath.components';
import {
  isEntityNotesEnabled,
  makeGenerateEntityNotesFromDocumentSummaryInput,
} from '@/modules/entities/components/EntityNotesPerDeath/EntityNotesPerDeath.util';
import { GenerateAINotesModal } from '@/modules/entities/components/EntityNotesPerDeath/GenerateAINotesModal';
import {
  useGenerateEntityNotesFromDocumentSummaryMutation,
  useSyncDeathOrderNotesMutation,
} from '@/modules/entities/components/EntityNotesPerDeath/graphql/GenerateEntityNotesFromDocumentSummary.generated';
import { Operations as GetHouseholdDocumentsOperations } from '@/modules/entities/components/EntityNotesPerDeath/graphql/GetHouseholdDocuments.generated';
import { FIRST_DECEDENT_ID_QUERY_PARAM } from '@/modules/entityDiagram/EntityDiagram.constants';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { diagnostics } from '@/utils/diagnostics';
import { formatDateToMonDDYYYY } from '@/utils/formatting/dates';

import { useEntityPageDefinition } from '../../details/EntityDetailOverviewPage';
import {
  EntityDetail_EntityFragment,
  Operations as EntityDetailPageOperations,
} from '../../details/graphql/EntityDetailPage.generated';
import {
  getDeathOrderNotesForDecedent,
  isEntityTimeBound,
} from '../../utils/getEntityNotesForDeath';
import { EntityPreviewModal } from '../EntityPreviewModal/EntityPreviewModal';
import { EntitySlideType } from '../ExportEntityBundleModal/ExportEntityBundleModal.types';

interface EntityNotesPerDeathInnerProps {
  entity: EntityDetail_EntityFragment;
}

function EntityNotesPerDeathInner({ entity }: EntityNotesPerDeathInnerProps) {
  const trackUserEvent = useTrackUserEvent();
  const { showFeedback } = useFeedback();
  const { primaryClients, isTwoClientHousehold } = useHouseholdDetailsContext();
  const [{ isModalOpen }, { openModal, closeModal }] = useModalState();
  const [
    { isModalOpen: isGenerateAiNotesModalOpen },
    {
      openModal: openGenerateAiNotesModal,
      closeModal: closeGenerateAiNotesModal,
    },
  ] = useModalState();
  const [
    { isModalOpen: isAddToPresentationModalOpen },
    {
      openModal: openAddToPresentationModal,
      closeModal: closeAddToPresentationModal,
    },
  ] = useModalState();

  const entityId = entity.id;
  const householdId = entity.household.id;

  const tabOptions: ButtonGroupInputOption<string>[] = useMemo(
    () =>
      primaryClients?.map<ButtonGroupInputOption<string>>((client) => ({
        display: `${client.displayGivenName} dies first`,
        value: client.id,
      })) ?? [],
    [primaryClients]
  );

  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const { activeTab: currentTab, setActiveTab: setCurrentTab } =
    useActiveTab<string>({
      defaultTab: (() => {
        const firstDecedentId = urlSearchParams.get(
          FIRST_DECEDENT_ID_QUERY_PARAM
        );
        if (
          firstDecedentId &&
          primaryClients?.some((client) => client.id === firstDecedentId)
        ) {
          return firstDecedentId;
        }

        return tabOptions[0]?.value ?? '';
      })(),
      filterSearchParam: FIRST_DECEDENT_ID_QUERY_PARAM,
    });

  const handleTabChange = useCallback(
    (_: SyntheticEvent, value: string) => {
      setCurrentTab(value);
      setUrlSearchParams({ firstDecedentId: value });
    },
    [setCurrentTab, setUrlSearchParams]
  );

  const isTimeBoundEntity = isEntityTimeBound(entity);
  const deathOrderNotesForFirstDecedent = getDeathOrderNotesForDecedent(
    entity,
    currentTab
  );
  const notesForDeath = deathOrderNotesForFirstDecedent?.notes ?? '';
  const entityDisplayName =
    entity.subtype?.displayName ?? 'Client presentation notes';
  const updatedBy = deathOrderNotesForFirstDecedent?.user?.displayName;
  const updatedAt = deathOrderNotesForFirstDecedent?.updatedAt;
  const [generatedAICredit, setGeneratedAICredit] = useState<string>('');
  useEffect(() => {
    setGeneratedAICredit(
      updatedAt
        ? `Edited by ${updatedBy!} on ${formatDateToMonDDYYYY(updatedAt)}`
        : ''
    );
  }, [updatedAt, updatedBy, deathOrderNotesForFirstDecedent]);

  const enableAIEntityNotes = useMemo(
    () => isEntityNotesEnabled(entity.kind),
    [entity.kind]
  );

  const isBeneficiaryDesignation = isBeneficiaryDesignationEntity(entity.kind);

  const [specifyNotesPerClient, setSpecifyNotesPerClient] = useState<boolean>(
    !entity.syncDeathOrderNotes
  );
  const [syncDeathOrderNotes] = useSyncDeathOrderNotesMutation();
  const handleSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const specifyNotesPerClient = e.target.checked;
    if (specifyNotesPerClient) {
      trackUserEvent('entity_notes specify notes per client toggled on', {
        entityId,
      });
    }
    setSpecifyNotesPerClient(specifyNotesPerClient);
    await syncDeathOrderNotes({
      variables: {
        input: {
          entityID: entityId,
          householdID: householdId,
          sync: !specifyNotesPerClient,
          activeDeathOrderID: currentTab,
        },
      },
      onError: (err) => {
        diagnostics.error('Failed to sync or unsync death order notes', err);
        showFeedback('Failed to sync or unsync death order notes');
      },
    });
  };
  const [generateEntityNotesFromDocumentSummary, { loading: isGenerating }] =
    useGenerateEntityNotesFromDocumentSummaryMutation();

  const handleGenerate = async (documentSummaryId: string) => {
    trackUserEvent('entity_notes generate clicked');
    closeGenerateAiNotesModal();
    await generateEntityNotesFromDocumentSummary({
      refetchQueries: [
        EntityDetailPageOperations.Query.EntityDetails,
        GetHouseholdDocumentsOperations.Query.GetHouseholdDocuments,
      ],
      variables: {
        input: makeGenerateEntityNotesFromDocumentSummaryInput({
          documentSummaryId,
          entityId,
          householdId,
          isTimeBoundEntity,
          activeDeathOrderId: currentTab,
          syncDeathOrderNotes: entity.syncDeathOrderNotes,
        }),
      },
      onCompleted: () => {
        setGeneratedAICredit(
          `Created by AI on ${formatDateToMonDDYYYY(new Date())}`
        );
      },
      onError: (err) => {
        diagnostics.error(
          'Failed to create AI generated notes from document summary',
          err
        );
        showFeedback(
          'Failed to create AI generated notes from document summary'
        );
      },
    });
  };

  const showDeathOrderChooser =
    isTwoClientHousehold && !isTimeBoundEntity && !isBeneficiaryDesignation;
  const showClientProfileTabs = enableAIEntityNotes
    ? showDeathOrderChooser && specifyNotesPerClient
    : showDeathOrderChooser;

  return (
    <TabsProvider currentTab={currentTab}>
      <Card variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            <Stack>
              <Typography variant="title-s">
                Client presentation notes
              </Typography>
              <Typography variant="body-xs">
                Add custom notes that can be displayed in client presentations
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              {showClientProfileTabs && (
                <ButtonGroup
                  value={currentTab}
                  label="Client"
                  options={tabOptions}
                  variant="light"
                  onChange={handleTabChange}
                  hideLabel
                />
              )}
            </Stack>
          </Stack>
          <Card
            variant="outlined"
            sx={{ minHeight: 240, position: 'relative' }}
          >
            {enableAIEntityNotes ? (
              <EntityNotesPerDeathEditorV2
                notesForDeath={notesForDeath}
                openEditor={openModal}
                isGenerating={isGenerating}
                onAddToPresentation={openAddToPresentationModal}
              />
            ) : (
              <EntityNotesPerDeathEditor
                notesForDeath={notesForDeath}
                openEditor={openModal}
              />
            )}
          </Card>
          {enableAIEntityNotes && (
            <EntityNotesPerDeathEditorFooter
              openModal={openModal}
              openGenerateAiNotesModal={openGenerateAiNotesModal}
              isGenerating={isGenerating}
              specifyNotesPerClient={specifyNotesPerClient}
              handleSwitchChange={handleSwitchChange}
              generatedCredit={generatedAICredit}
              showToggle={showDeathOrderChooser}
              addNotesCopy={notesForDeath ? 'Edit notes' : 'Add notes'}
            />
          )}
        </Stack>
      </Card>
      {isModalOpen && (
        <EntityPreviewModal
          onClose={closeModal}
          entityId={entityId}
          householdId={householdId}
          initialSlide={EntitySlideType.DiagramAndNotesSlide}
          deathOrderId={currentTab}
        />
      )}
      {enableAIEntityNotes && (
        <GenerateAINotesModal
          householdId={householdId}
          entityId={entityId}
          entityKind={entity.kind}
          isOpen={isGenerateAiNotesModalOpen}
          handleClose={closeGenerateAiNotesModal}
          handleGenerate={handleGenerate}
          isGenerating={isGenerating}
        />
      )}
      {isAddToPresentationModalOpen && (
        <AddNotesToPresentationModal
          entityDisplayName={entityDisplayName}
          householdId={householdId}
          isOpen={isAddToPresentationModalOpen}
          notes={notesForDeath}
          onClose={closeAddToPresentationModal}
        />
      )}
    </TabsProvider>
  );
}

export interface EntityNotesPerDeathProps {
  entityId: string;
}

export function EntityNotesPerDeath({ entityId }: EntityNotesPerDeathProps) {
  const { entity } = useEntityPageDefinition(entityId);

  if (!entity) return null;

  return <EntityNotesPerDeathInner entity={entity} />;
}
